import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../utils/api';
import { Plus, Trash2, Edit2, X, Check, Upload, Film, Image, Youtube } from 'lucide-react';
import { extractYoutubeId, youtubeThumb } from '../../utils/youtube';
import { useTranscodeJobs, StreamingBadge, useTranscodeCapacity, CapacityNote } from './StreamingStatus';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Progress bar component
const UploadProgressBar = ({ progress, loaded, total, label }) => {
  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  return (
    <div className="mt-3">
      <div className="flex justify-between text-sm mb-1">
        <span className="text-gray-600">{label}</span>
        <span className="text-gray-500">
          {formatSize(loaded)} / {formatSize(total)} ({progress}%)
        </span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-300 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>
      {progress === 100 && (
        <p className="text-sm text-green-600 mt-1">Processing upload...</p>
      )}
    </div>
  );
};

const FilmSettingsPanel = () => {
  const [activeSubTab, setActiveSubTab] = useState('settings');
  const [filmSettings, setFilmSettings] = useState({
    preloader_gif_url: '',
    default_tags: []
  });
  const [tags, setTags] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [frameSeconds, setFrameSeconds] = useState(4);
  const [capturing, setCapturing] = useState(false);
  const { jobs: transcodeJobs, active: transcodeActive, refresh: refreshJobs } = useTranscodeJobs();
  const capacity = useTranscodeCapacity();
  const prevActive = useRef(false);
  useEffect(() => {
    if (prevActive.current && !transcodeActive) fetchData();
    prevActive.current = transcodeActive;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcodeActive]);

  const prepareStreaming = async (videoId, maxHeight) => {
    try {
      const q = `force=true${maxHeight ? `&max_height=${maxHeight}` : ''}`;
      await api.request(`/api/admin/film-videos/${videoId}/transcode?${q}`, { method: 'POST' });
      setMessage('Encoding queued — this takes a few minutes per film.');
      refreshJobs();
    } catch (error) { setMessage(`Failed: ${error.message}`); }
  };
  const prepareAll = async () => {
    try {
      const r = await api.request('/api/admin/film-videos/transcode-all', { method: 'POST' });
      setMessage(r.queued === 0 ? 'All films are already prepared for streaming' : `Queued ${r.queued} film(s) for encoding`);
      refreshJobs();
    } catch (error) { setMessage(`Failed: ${error.message}`); }
  };
  const [uploading, setUploading] = useState({ video: false, thumbnail: false, preloader: false });
  const [uploadProgress, setUploadProgress] = useState({ video: 0, thumbnail: 0, preloader: 0 });
  const [uploadDetails, setUploadDetails] = useState({ video: { loaded: 0, total: 0 }, thumbnail: { loaded: 0, total: 0 }, preloader: { loaded: 0, total: 0 } });
  
  // Modal states
  const [showTagModal, setShowTagModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [editingVideo, setEditingVideo] = useState(null);
  const [tagForm, setTagForm] = useState({ name: '', display_name: '', order: 0 });
  const [videoForm, setVideoForm] = useState({
    title: '', subtitle: '', video_url: '', video_url_low: '', video_url_high: '', youtube_id: '', thumbnail_url: '', tags: [], priority: 0, is_featured: false, order: 0
  });

  // File input refs
  const videoInputRef = useRef(null);
  const thumbnailInputRef = useRef(null);
  const preloaderInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, tagsRes, videosRes] = await Promise.all([
        fetch(`${API_URL}/api/content/film-settings`),
        fetch(`${API_URL}/api/content/film-tags`),
        fetch(`${API_URL}/api/content/film-videos`)
      ]);
      
      const settingsData = await settingsRes.json();
      const tagsData = await tagsRes.json();
      const videosData = await videosRes.json();
      
      setFilmSettings(settingsData);
      setTags(tagsData);
      setVideos(videosData);
    } catch (error) {
      console.error('Error fetching film data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    setMessage('');
    try {
      const res = await fetch(`${API_URL}/api/content/film-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${api.getToken()}`
        },
        body: JSON.stringify(filmSettings)
      });
      
      if (res.ok) {
        setMessage('Settings saved!');
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error) {
      setMessage('Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  // File upload handlers with progress
  const handleFileUpload = async (file, type) => {
    if (!file) return null;
    
    setUploading(prev => ({ ...prev, [type]: true }));
    setUploadProgress(prev => ({ ...prev, [type]: 0 }));
    setUploadDetails(prev => ({ ...prev, [type]: { loaded: 0, total: file.size } }));
    
    const onProgress = (percent, loaded, total) => {
      setUploadProgress(prev => ({ ...prev, [type]: percent }));
      setUploadDetails(prev => ({ ...prev, [type]: { loaded, total } }));
    };
    
    try {
      let result;
      if (type === 'video') {
        result = await api.uploadVideo(file, onProgress);
      } else {
        result = await api.uploadImageWithProgress(file, type === 'thumbnail' ? 'thumbnails' : 'preloaders', onProgress);
      }
      return result.url;
    } catch (error) {
      console.error(`Error uploading ${type}:`, error);
      setMessage(`Error uploading ${type}: ${error.message}`);
      return null;
    } finally {
      setUploading(prev => ({ ...prev, [type]: false }));
      setUploadProgress(prev => ({ ...prev, [type]: 0 }));
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('video/')) {
      setMessage('Please select a video file');
      return;
    }

    // Warn for large files
    if (file.size > 500 * 1024 * 1024) {
      setMessage(`Uploading large file (${(file.size / (1024*1024)).toFixed(0)}MB). This may take a while...`);
    }
    
    const url = await handleFileUpload(file, 'video');
    if (url) {
      setVideoForm(prev => ({ ...prev, video_url: url }));
      setMessage('Video uploaded successfully!');
      setTimeout(() => setMessage(''), 3000);
    }
    
    // Reset file input
    e.target.value = '';
  };

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      setMessage('Please select an image file');
      return;
    }
    
    const url = await handleFileUpload(file, 'thumbnail');
    if (url) {
      setVideoForm(prev => ({ ...prev, thumbnail_url: url }));
      setMessage('Thumbnail uploaded successfully!');
      setTimeout(() => setMessage(''), 3000);
    }
    
    // Reset file input
    e.target.value = '';
  };

  const handlePreloaderUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const url = await handleFileUpload(file, 'preloader');
    if (url) {
      setFilmSettings(prev => ({ ...prev, preloader_gif_url: url }));
      setMessage('Preloader uploaded! Click Save to apply.');
    }
    
    // Reset file input
    e.target.value = '';
  };

  // Tag CRUD
  const handleSaveTag = async () => {
    try {
      const url = editingTag 
        ? `${API_URL}/api/content/film-tags/${editingTag.id}`
        : `${API_URL}/api/content/film-tags`;
      
      const res = await fetch(url, {
        method: editingTag ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${api.getToken()}`
        },
        body: JSON.stringify(tagForm)
      });
      
      if (res.ok) {
        fetchData();
        setShowTagModal(false);
        setEditingTag(null);
        setTagForm({ name: '', display_name: '', order: 0 });
      }
    } catch (error) {
      console.error('Error saving tag:', error);
    }
  };

  const handleDeleteTag = async (tagId) => {
    if (!window.confirm('Delete this tag?')) return;
    try {
      await fetch(`${API_URL}/api/content/film-tags/${tagId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${api.getToken()}` }
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting tag:', error);
    }
  };

  // Video CRUD
  const handleSaveVideo = async () => {
    try {
      const url = editingVideo 
        ? `${API_URL}/api/content/film-videos/${editingVideo.id}`
        : `${API_URL}/api/content/film-videos`;
      
      const res = await fetch(url, {
        method: editingVideo ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${api.getToken()}`
        },
        body: JSON.stringify(videoForm)
      });
      
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        const savedId = editingVideo?.id || data?.video?.id;
        if (!videoForm.thumbnail_url && savedId && (videoForm.video_url || videoForm.youtube_id)) {
          setMessage('Saved — capturing a thumbnail from the video…');
          try {
            await api.request(`/api/admin/film-videos/${savedId}/generate-thumbnail?seconds=${frameSeconds}`, { method: 'POST' });
          } catch (e) {
            console.error('Auto thumbnail failed:', e);
          }
        }
        fetchData();
        setShowVideoModal(false);
        setEditingVideo(null);
        setVideoForm({ title: '', subtitle: '', video_url: '', video_url_low: '', video_url_high: '', youtube_id: '', thumbnail_url: '', tags: [], priority: 0, is_featured: false, order: 0 });
        setMessage('Video saved successfully!');
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error saving video:', error);
    }
  };

  const handleCaptureFrame = async () => {
    if (!editingVideo?.id) return;
    setCapturing(true);
    try {
      const r = await api.request(`/api/admin/film-videos/${editingVideo.id}/generate-thumbnail?seconds=${frameSeconds}`, { method: 'POST' });
      setVideoForm(prev => ({ ...prev, thumbnail_url: r.thumbnail_url }));
      setMessage(`Thumbnail captured at ${frameSeconds}s`);
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage(`Capture failed: ${error.message}`);
    } finally {
      setCapturing(false);
    }
  };

  const handleGenerateMissing = async () => {
    setCapturing(true);
    setMessage('Generating thumbnails for videos without one…');
    try {
      const r = await api.request('/api/admin/film-videos/generate-missing-thumbnails?seconds=4', { method: 'POST' });
      setMessage(r.processed === 0 ? 'All videos already have thumbnails' : `Generated ${r.generated}/${r.processed} thumbnail(s)`);
      fetchData();
    } catch (error) {
      setMessage(`Failed: ${error.message}`);
    } finally {
      setCapturing(false);
      setTimeout(() => setMessage(''), 4000);
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm('Delete this video?')) return;
    try {
      await fetch(`${API_URL}/api/content/film-videos/${videoId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${api.getToken()}` }
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting video:', error);
    }
  };

  const toggleVideoTag = (tagName) => {
    setVideoForm(prev => ({
      ...prev,
      tags: prev.tags.includes(tagName)
        ? prev.tags.filter(t => t !== tagName)
        : [...prev.tags, tagName]
    }));
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold">Film Theatre Settings</h2>
          <p className="text-sm text-gray-500 mt-1">Configure the IMAX theatre experience</p>
        </div>
      </div>

      {message && (
        <div className={`mb-4 p-3 rounded text-sm ${message.includes('Error') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
          {message}
        </div>
      )}

      {/* Sub-tabs */}
      <div className="flex gap-4 mb-6 border-b">
        {[
          { id: 'settings', label: 'General Settings' },
          { id: 'tags', label: 'Tags' },
          { id: 'videos', label: 'Film Videos' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveSubTab(tab.id)}
            className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeSubTab === tab.id
                ? 'border-black text-black'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Settings Tab */}
      {activeSubTab === 'settings' && (
        <div className="space-y-6">
          <div className="border rounded-lg p-6">
            <h3 className="font-medium mb-4">Preloader</h3>
            <div>
              <label className="block text-sm font-medium mb-2">Preloader GIF/Image</label>
              
              {filmSettings.preloader_gif_url ? (
                <div className="flex items-start gap-4 mb-4">
                  <div className="bg-gray-900 p-4 rounded">
                    <img 
                      src={filmSettings.preloader_gif_url} 
                      alt="Preloader preview" 
                      className="h-20 w-auto"
                    />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-500 mb-2">Current preloader</p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => preloaderInputRef.current?.click()}
                        disabled={uploading.preloader}
                        className="px-4 py-2 text-sm border rounded hover:bg-gray-50"
                      >
                        {uploading.preloader ? 'Uploading...' : 'Replace'}
                      </button>
                      <button
                        onClick={() => setFilmSettings(prev => ({ ...prev, preloader_gif_url: '' }))}
                        className="px-4 py-2 text-sm border border-red-200 text-red-600 rounded hover:bg-red-50"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div 
                  onClick={() => preloaderInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-gray-400 transition-colors"
                >
                  <Upload size={32} className="mx-auto text-gray-400 mb-3" />
                  <p className="text-sm text-gray-600 mb-1">
                    {uploading.preloader ? 'Uploading...' : 'Click to upload preloader GIF'}
                  </p>
                  <p className="text-xs text-gray-400">GIF, PNG, or WebP recommended</p>
                </div>
              )}
              
              <input
                ref={preloaderInputRef}
                type="file"
                accept="image/*"
                onChange={handlePreloaderUpload}
                className="hidden"
              />
              
              <p className="text-xs text-gray-500 mt-2">
                Leave empty to show the logo with a loading bar
              </p>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={handleSaveSettings}
              disabled={saving}
              className="px-6 py-2 bg-black text-white rounded hover:bg-gray-800 transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      )}

      {/* Tags Tab */}
      {activeSubTab === 'tags' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-gray-500">Manage tags for filtering film videos</p>
            <button
              onClick={() => {
                setEditingTag(null);
                setTagForm({ name: '', display_name: '', order: tags.length });
                setShowTagModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-black text-white text-sm rounded hover:bg-gray-800"
            >
              <Plus size={16} /> Add Tag
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {tags.map(tag => (
              <div key={tag.id} className="border rounded-lg p-4 flex items-center justify-between">
                <div>
                  <p className="font-medium">{tag.display_name}</p>
                  <p className="text-xs text-gray-400">{tag.name}</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setEditingTag(tag);
                      setTagForm({ name: tag.name, display_name: tag.display_name, order: tag.order });
                      setShowTagModal(true);
                    }}
                    className="p-1 text-gray-400 hover:text-black"
                  >
                    <Edit2 size={14} />
                  </button>
                  <button
                    onClick={() => handleDeleteTag(tag.id)}
                    className="p-1 text-gray-400 hover:text-red-500"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Videos Tab */}
      {activeSubTab === 'videos' && (
        <div>
          <CapacityNote capacity={capacity} />
          <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
            <p className="text-sm text-gray-500">Manage film videos for the theatre experience</p>
            <div className="flex gap-2 flex-wrap">
            {videos.some(v => !v.hls_url && (v.video_url || v.video_url_high || v.video_url_low)) && (
              <button
                onClick={prepareAll}
                disabled={transcodeActive}
                data-testid="films-prepare-all-btn"
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-sm rounded hover:bg-gray-50 disabled:opacity-50"
              >
                {transcodeActive ? 'Encoding…' : 'Prepare all for streaming'}
              </button>
            )}
            {videos.some(v => !v.thumbnail_url) && (
              <button
                onClick={handleGenerateMissing}
                disabled={capturing}
                data-testid="films-generate-missing-btn"
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-sm rounded hover:bg-gray-50 disabled:opacity-50"
              >
                <Image size={16} /> {capturing ? 'Generating…' : 'Generate missing thumbnails'}
              </button>
            )}
            <button
              onClick={() => {
                setEditingVideo(null);
                setVideoForm({ title: '', subtitle: '', video_url: '', video_url_low: '', video_url_high: '', youtube_id: '', thumbnail_url: '', tags: [], priority: 0, is_featured: false, order: videos.length });
                setShowVideoModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-black text-white text-sm rounded hover:bg-gray-800"
            >
              <Plus size={16} /> Add Video
            </button>
            </div>
          </div>

          <div className="space-y-4">
            {videos.sort((a, b) => (b.priority || 0) - (a.priority || 0)).map(video => (
              <div key={video.id} className={`border rounded-lg p-4 flex gap-4 ${video.is_featured ? 'border-yellow-400 bg-yellow-50/50' : ''}`}>
                <div className="w-40 h-24 bg-gray-100 rounded overflow-hidden flex-shrink-0 relative">
                  {video.thumbnail_url ? (
                    <img src={video.thumbnail_url} alt={video.title} className="w-full h-full object-cover" data-testid={`film-video-thumb-${video.id}`} />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 text-xs gap-1" data-testid={`film-video-nothumb-${video.id}`}>
                      <span>No thumbnail</span>
                      <span className="text-[9px] uppercase tracking-wider text-amber-600 text-center px-1">Hidden from Google video</span>
                    </div>
                  )}
                  {video.is_featured && (
                    <span className="absolute top-1 left-1 px-1.5 py-0.5 bg-yellow-400 text-black text-[10px] font-bold rounded">MAIN</span>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium">{video.title}</h4>
                    {video.priority > 0 && (
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-[10px] rounded font-medium">Priority: {video.priority}</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">{video.subtitle}</p>
                  {video.video_url && (
                    <p className="text-xs text-green-600 mt-1">✓ Video uploaded</p>
                  )}
                  <div className="mt-1.5">
                    <StreamingBadge target={video} job={transcodeJobs[video.id]} onPrepare={(mh) => prepareStreaming(video.id, mh)} />
                  </div>
                  {video.tags && video.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {video.tags.map(tag => (
                        <span key={tag} className="px-2 py-0.5 bg-gray-100 text-xs rounded">{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setEditingVideo(video);
                      setVideoForm({
                        title: video.title,
                        subtitle: video.subtitle || '',
                        video_url: video.video_url || '',
                        video_url_low: video.video_url_low || '',
                        video_url_high: video.video_url_high || '',
                        youtube_id: video.youtube_id || '',
                        thumbnail_url: video.thumbnail_url || '',
                        tags: video.tags || [],
                        priority: video.priority || 0,
                        is_featured: video.is_featured || false,
                        order: video.order || 0
                      });
                      setShowVideoModal(true);
                    }}
                    className="p-2 text-gray-400 hover:text-black"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => handleDeleteVideo(video.id)}
                    className="p-2 text-gray-400 hover:text-red-500"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
            {videos.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                No videos yet. Add your first film video.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tag Modal */}
      {showTagModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium">{editingTag ? 'Edit Tag' : 'Add Tag'}</h3>
              <button onClick={() => setShowTagModal(false)} className="text-gray-400 hover:text-black">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Display Name</label>
                <input
                  type="text"
                  value={tagForm.display_name}
                  onChange={(e) => {
                    setTagForm(prev => ({ 
                      ...prev, 
                      display_name: e.target.value,
                      name: e.target.value.toLowerCase().replace(/\s+/g, '_')
                    }));
                  }}
                  className="w-full px-3 py-2 border rounded"
                  placeholder="e.g., Pakistani"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Tag Key</label>
                <input
                  type="text"
                  value={tagForm.name}
                  onChange={(e) => setTagForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border rounded bg-gray-50"
                  placeholder="e.g., pakistani"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowTagModal(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-black"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTag}
                className="px-4 py-2 bg-black text-white text-sm rounded hover:bg-gray-800"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Video Modal */}
      {showVideoModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium">{editingVideo ? 'Edit Video' : 'Add Video'}</h3>
              <button onClick={() => setShowVideoModal(false)} className="text-gray-400 hover:text-black">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Title *</label>
                  <input
                    type="text"
                    value={videoForm.title}
                    onChange={(e) => setVideoForm(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 border rounded"
                    placeholder="Couple Name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Subtitle</label>
                  <input
                    type="text"
                    value={videoForm.subtitle}
                    onChange={(e) => setVideoForm(prev => ({ ...prev, subtitle: e.target.value }))}
                    className="w-full px-3 py-2 border rounded"
                    placeholder="Wedding Location"
                  />
                </div>
              </div>
              
              {/* Video Upload */}
              <div>
                <label className="block text-sm font-medium mb-2">Video File</label>
                {videoForm.video_url ? (
                  <div className="flex items-center gap-4 p-4 bg-green-50 border border-green-200 rounded">
                    <Film size={24} className="text-green-600" />
                    <div className="flex-1">
                      <p className="text-sm text-green-800">Video uploaded</p>
                      <p className="text-xs text-green-600 truncate">{videoForm.video_url.split('/').pop()}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => videoInputRef.current?.click()}
                        disabled={uploading.video}
                        className="px-3 py-1 text-sm border rounded hover:bg-white"
                      >
                        Replace
                      </button>
                      <button
                        onClick={() => setVideoForm(prev => ({ ...prev, video_url: '' }))}
                        className="px-3 py-1 text-sm text-red-600 border border-red-200 rounded hover:bg-red-50"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : uploading.video ? (
                  <div className="border-2 border-blue-300 bg-blue-50 rounded-lg p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                      <p className="text-sm font-medium text-blue-700">Uploading video...</p>
                    </div>
                    <UploadProgressBar 
                      progress={uploadProgress.video}
                      loaded={uploadDetails.video.loaded}
                      total={uploadDetails.video.total}
                      label="Video upload progress"
                    />
                  </div>
                ) : (
                  <div 
                    onClick={() => videoInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-gray-400 transition-colors"
                  >
                    <Upload size={28} className="mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">Click to upload video</p>
                    <p className="text-xs text-gray-400 mt-1">MP4, MOV, WebM supported (max 2GB)</p>
                  </div>
                )}
                <input
                  ref={videoInputRef}
                  type="file"
                  accept="video/*"
                  onChange={handleVideoUpload}
                  className="hidden"
                />
                
                {/* Or paste URL */}
                <div className="mt-2">
                  <p className="text-xs text-gray-500 mb-1">Or paste video URL (Standard / default quality):</p>
                  <input
                    type="url"
                    value={videoForm.video_url}
                    onChange={(e) => setVideoForm(prev => ({ ...prev, video_url: e.target.value }))}
                    className="w-full px-3 py-2 border rounded text-sm"
                    placeholder="https://example.com/video.mp4"
                  />
                </div>

                {/* Multi-quality (auto adaptive) */}
                <div className="mt-4 p-3 bg-blue-50 border border-blue-100 rounded">
                  <p className="text-sm font-medium text-blue-900 mb-1">Adaptive quality (optional)</p>
                  <p className="text-xs text-blue-700/70 mb-3">
                    Upload mobile + high variants of the same film. The site auto-picks the right one based on the viewer&apos;s connection:
                    cellular users get <em>low</em>, fast Wi-Fi gets <em>high</em>, everyone else gets <em>standard</em>. Any field left empty just falls back to the others.
                  </p>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-blue-900 mb-1">Mobile / low (480p, smallest file)</label>
                      <input
                        type="url"
                        value={videoForm.video_url_low}
                        onChange={(e) => setVideoForm(prev => ({ ...prev, video_url_low: e.target.value }))}
                        className="w-full px-3 py-2 border border-blue-200 rounded text-sm"
                        placeholder="https://example.com/film_480p.mp4"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-blue-900 mb-1">High / desktop (1080p, largest file)</label>
                      <input
                        type="url"
                        value={videoForm.video_url_high}
                        onChange={(e) => setVideoForm(prev => ({ ...prev, video_url_high: e.target.value }))}
                        className="w-full px-3 py-2 border border-blue-200 rounded text-sm"
                        placeholder="https://example.com/film_1080p.mp4"
                      />
                    </div>
                  </div>
                </div>

                {/* YouTube URL — preferred for mobile / cellular streaming */}
                <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded">
                  <div className="flex items-center gap-2 mb-2">
                    <Youtube size={18} className="text-red-600" />
                    <p className="text-sm font-medium text-red-900">YouTube source (recommended for cellular)</p>
                  </div>
                  <p className="text-xs text-red-700/70 mb-2">
                    Paste a YouTube URL or 11-character video ID. When set, this takes priority over the AWS file above and streams adaptively (perfect for mobile data).
                  </p>
                  <input
                    type="text"
                    value={videoForm.youtube_id}
                    onChange={(e) => {
                      const raw = e.target.value;
                      // Auto-extract the 11-char ID if the user pastes a full URL
                      const id = extractYoutubeId(raw) || raw;
                      setVideoForm(prev => ({ ...prev, youtube_id: id }));
                    }}
                    className="w-full px-3 py-2 border border-red-200 rounded text-sm"
                    placeholder="https://www.youtube.com/watch?v=...  or  11-char ID"
                  />
                  {videoForm.youtube_id && /^[a-zA-Z0-9_-]{11}$/.test(videoForm.youtube_id) && (
                    <div className="flex items-center gap-3 mt-3 p-2 bg-white rounded">
                      <img src={youtubeThumb(videoForm.youtube_id)} alt="YouTube preview" className="w-24 h-14 object-cover" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-green-700 font-medium">✓ Valid YouTube ID</p>
                        <p className="text-xs text-gray-500 truncate">{videoForm.youtube_id}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setVideoForm(prev => ({ ...prev, youtube_id: '' }))}
                        className="text-xs text-red-600 hover:underline"
                      >
                        Clear
                      </button>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Thumbnail Upload */}
              <div>
                <label className="block text-sm font-medium mb-2">Thumbnail Image</label>
                {videoForm.thumbnail_url ? (
                  <div className="flex items-start gap-4">
                    <div className="w-32 h-20 bg-gray-100 rounded overflow-hidden">
                      <img 
                        src={videoForm.thumbnail_url} 
                        alt="Thumbnail" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-600 mb-2">Thumbnail uploaded</p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => thumbnailInputRef.current?.click()}
                          disabled={uploading.thumbnail}
                          className="px-3 py-1 text-sm border rounded hover:bg-gray-50"
                        >
                          Replace
                        </button>
                        <button
                          onClick={() => setVideoForm(prev => ({ ...prev, thumbnail_url: '' }))}
                          className="px-3 py-1 text-sm text-red-600 border border-red-200 rounded hover:bg-red-50"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ) : uploading.thumbnail ? (
                  <div className="border-2 border-blue-300 bg-blue-50 rounded-lg p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                      <p className="text-sm font-medium text-blue-700">Uploading thumbnail...</p>
                    </div>
                    <UploadProgressBar 
                      progress={uploadProgress.thumbnail}
                      loaded={uploadDetails.thumbnail.loaded}
                      total={uploadDetails.thumbnail.total}
                      label="Thumbnail upload progress"
                    />
                  </div>
                ) : (
                  <div 
                    onClick={() => thumbnailInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-gray-400 transition-colors"
                  >
                    <Image size={28} className="mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">Click to upload thumbnail</p>
                    <p className="text-xs text-gray-400 mt-1">JPG, PNG, WebP supported · or leave empty and we'll capture a frame on save</p>
                  </div>
                )}
                {(videoForm.video_url || videoForm.youtube_id) && (
                  <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-gray-600" data-testid="film-capture-frame-row">
                    <span>Capture a frame from the video at</span>
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      value={frameSeconds}
                      onChange={(e) => setFrameSeconds(Number(e.target.value))}
                      className="w-16 px-2 py-1 border border-gray-300 rounded text-xs"
                      data-testid="film-frame-seconds"
                    />
                    <span>s</span>
                    <button
                      type="button"
                      onClick={handleCaptureFrame}
                      disabled={capturing || !editingVideo?.id}
                      title={!editingVideo?.id ? 'Save the video first' : ''}
                      className="px-3 py-1 border border-black rounded hover:bg-black hover:text-white transition-colors disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-inherit"
                      data-testid="film-capture-frame-btn"
                    >
                      {capturing ? 'Capturing…' : editingVideo?.id ? 'Capture frame' : 'Save first to capture'}
                    </button>
                  </div>
                )}
                <input
                  ref={thumbnailInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleThumbnailUpload}
                  className="hidden"
                />
                
                {/* Or paste URL */}
                <div className="mt-2">
                  <p className="text-xs text-gray-500 mb-1">Or paste thumbnail URL:</p>
                  <input
                    type="url"
                    value={videoForm.thumbnail_url}
                    onChange={(e) => setVideoForm(prev => ({ ...prev, thumbnail_url: e.target.value }))}
                    className="w-full px-3 py-2 border rounded text-sm"
                    placeholder="https://example.com/thumbnail.jpg"
                  />
                </div>
              </div>
              
              {/* Tags */}
              <div>
                <label className="block text-sm font-medium mb-2">Tags</label>
                <div className="flex flex-wrap gap-2">
                  {tags.map(tag => (
                    <button
                      key={tag.id}
                      type="button"
                      onClick={() => toggleVideoTag(tag.name)}
                      className={`px-3 py-1 text-sm rounded border transition-colors ${
                        videoForm.tags.includes(tag.name)
                          ? 'bg-black text-white border-black'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                      }`}
                    >
                      {videoForm.tags.includes(tag.name) && <Check size={12} className="inline mr-1" />}
                      {tag.display_name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Priority & Featured */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Priority</label>
                  <input
                    type="number"
                    value={videoForm.priority}
                    onChange={(e) => setVideoForm(prev => ({ ...prev, priority: parseInt(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border rounded"
                    min="0"
                    max="100"
                  />
                  <p className="text-xs text-gray-400 mt-1">Higher number = plays first within its tag group</p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Main Video</label>
                  <button
                    type="button"
                    onClick={() => setVideoForm(prev => ({ ...prev, is_featured: !prev.is_featured }))}
                    className={`w-full px-3 py-2 rounded border text-sm transition-colors ${
                      videoForm.is_featured
                        ? 'bg-yellow-400 border-yellow-400 text-black font-medium'
                        : 'bg-white border-gray-200 text-gray-600 hover:border-gray-400'
                    }`}
                  >
                    {videoForm.is_featured ? '★ Featured — Plays First on "Play All"' : 'Set as Main Video'}
                  </button>
                  <p className="text-xs text-gray-400 mt-1">The main video plays first when "Play All" is selected</p>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowVideoModal(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-black"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveVideo}
                disabled={!videoForm.title}
                className="px-4 py-2 bg-black text-white text-sm rounded hover:bg-gray-800 disabled:opacity-50"
              >
                Save Video
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilmSettingsPanel;
